import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { structureApi, type PositionDetail, type PositionKind, type PositionRequest } from "../../api/structure";
import { locationsApi } from "../../api/locations";
import type { ApiError } from "../../api/client";
import { LOCATION_INDENT as INDENT, flattenLocations, type FlatLocation } from "../../lib/locationsFlat";
import { useLocationKinds } from "../../lib/useLocationKinds";
import { defaultChildKind, usePositionKinds } from "../../lib/usePositionKinds";
import { LocationKindIcon } from "../../components/LocationKindIcon";
import { Modal } from "../../components/Modal";
import { Select, type SelectOption } from "../../components/Select";
import { compactAttributeValues } from "../../components/AttributeValuesForm";
import { AssetTypeCombobox, type AssetTypeRef } from "../../components/AssetTypeCombobox";
import { NO_VALUE, errorBox, noticeBox, positionTitle, textarea, toRequest, translateError, type ModalState } from "./shared";

export function PositionFormModal({
  mode, onClose, onCreated, onEdited,
}: {
  mode: Extract<ModalState, { kind: "create" | "edit" }>;
  onClose: () => void;
  onCreated: (created: PositionDetail[]) => Promise<void>;
  onEdited: (saved: PositionDetail) => Promise<void>;
}) {
  const { t } = useTranslation();
  const editing = mode.kind === "edit" ? mode.position : null;
  const parent = mode.kind === "create" ? mode.parent : null;
  const topLevel = mode.kind === "create" && parent === null;

  const { kinds, labelOf, holdsAssetOf } = usePositionKinds();

  const [name, setName] = useState(editing?.name ?? "");
  const [code, setCode] = useState(editing?.code ?? "");
  // The codebook may still be loading on first open: pick the default once it arrives.
  const [kind, setKind] = useState<PositionKind>(editing ? editing.kind : defaultChildKind(kinds, parent?.kind ?? null));
  useEffect(() => {
    if (!kind && kinds.length > 0) setKind(defaultChildKind(kinds, parent?.kind ?? null));
  }, [kind, kinds, parent]);
  const [quantity, setQuantity] = useState("1");
  const [expectedType, setExpectedType] = useState<AssetTypeRef | null>(
    editing?.expectedTypeId
      ? { id: editing.expectedTypeId, code: editing.expectedTypeCode ?? "", name: editing.expectedTypeName ?? "" }
      : null,
  );
  // A kind that does not hold an asset has no expected type: the field is hidden and the value dropped.
  const kindHoldsAsset = holdsAssetOf(kind);
  useEffect(() => {
    if (!kindHoldsAsset) setExpectedType(null);
  }, [kindHoldsAsset]);
  const [locationId, setLocationId] = useState(editing?.locationId ?? NO_VALUE);
  const [drawingCode, setDrawingCode] = useState(editing?.drawingCode ?? "");
  const [interventionNote, setInterventionNote] = useState(editing?.interventionNote ?? "");
  const [note, setNote] = useState(editing?.note ?? "");
  const [sortOrder, setSortOrder] = useState(editing?.sortOrder?.toString() ?? "0");
  const [locations, setLocations] = useState<FlatLocation[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    locationsApi.tree().then((tree) => setLocations(flattenLocations(tree))).catch(() => setLocations([]));
  }, []);

  const kindOptions = useMemo<SelectOption<PositionKind>[]>(
    () => {
      const opts = kinds.map((k) => ({ value: k.code, label: k.name }));
      // A position whose kind was deleted from the codebook keeps it selectable until the user picks another.
      if (kind && !kinds.some((k) => k.code === kind)) opts.unshift({ value: kind, label: kind });
      return opts;
    },
    [kinds, kind],
  );
  const { iconOf: iconOfLocation } = useLocationKinds();
  const locationOptions = useMemo<SelectOption<string>[]>(
    () => [
      { value: NO_VALUE, label: t("structure.locationInherited", "— nasleđena od pretka —") },
      ...locations.map((l) => ({ value: l.id, label: `${INDENT.repeat(l.depth)}${l.name} (${l.code})`, keywords: l.pathLabel, left: <LocationKindIcon icon={iconOfLocation(l.kind)} /> })),
    ],
    [locations, t, iconOfLocation],
  );

  // Changing what is expected invalidates the stored reference values (server: UNKNOWN_ATTRIBUTE).
  const expectedChanged = !!editing && (editing.expectedTypeId ?? null) !== (expectedType?.id ?? null);
  const hasRefValues = !!editing && Object.keys(compactAttributeValues(editing.referenceValues)).length > 0;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const qty = Number.parseInt(quantity, 10);
    if (!editing && (!Number.isInteger(qty) || qty < 1 || qty > 50)) {
      setError(t("structure.error.quantity", "Količina mora biti ceo broj od 1 do 50."));
      return;
    }
    if (!kind) {
      setError(t("structure.error.kindRequired", "Izaberite vrstu pozicije."));
      return;
    }
    const fields = {
      code: code.trim() || null,
      name: name.trim(),
      kind,
      expectedTypeId: kindHoldsAsset ? expectedType?.id ?? null : null,
      locationId: locationId || null,
      drawingCode: drawingCode.trim() || null,
      interventionNote: interventionNote.trim() || null,
      note: note.trim() || null,
      sortOrder: Number.parseInt(sortOrder, 10) || 0,
    };
    setSubmitting(true);
    try {
      if (editing) {
        const body: PositionRequest = {
          ...toRequest(editing),
          ...fields,
          referenceValues: expectedChanged ? {} : compactAttributeValues(editing.referenceValues),
        };
        await onEdited(await structureApi.update(editing.id, body));
      } else {
        await onCreated(await structureApi.create({
          parentId: parent?.id ?? null,
          ...fields,
          quantity: qty,
        }));
      }
    } catch (err) {
      setError(translateError(t, err as ApiError, labelOf(kind)));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      title={editing
        ? t("structure.modal.editTitle", "Izmena pozicije")
        : topLevel ? t("structure.modal.createTopTitle", "Nova pozicija na prvom nivou") : t("structure.modal.createTitle", "Nova pod-pozicija")}
      subtitle={parent
        ? t("structure.modal.createSub", "Roditelj: {{name}}", { name: positionTitle(parent) })
        : topLevel ? t("structure.modal.createTopSub", "Bez roditelja — npr. firma, pogon ili objekat.") : undefined}
      onClose={onClose}
      width={640}
    >
      <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <label className="field">
          <span>{t("structure.field.name", "Naziv")} <span className="req-mark">*</span></span>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} required maxLength={200} />
        </label>
        <div className="form-grid">
          <label className="field">
            <span>{t("structure.field.code", "Šifra")}</span>
            <input className="input" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} maxLength={40} placeholder="D601" autoCapitalize="characters" style={{ fontFamily: "var(--mono)" }} />
          </label>
          <label className="field">
            <span>{t("structure.field.kind", "Vrsta")}</span>
            <Select<PositionKind> value={kind} options={kindOptions} onChange={setKind} ariaLabel={t("structure.field.kind", "Vrsta")} />
          </label>
        </div>
        {!editing && (
          <div className="form-grid">
            <label className="field">
              <span>{t("structure.field.quantity", "Količina")} <span className="req-mark">*</span></span>
              <input className="input" type="number" min={1} max={50} value={quantity} onChange={(e) => setQuantity(e.target.value)} inputMode="numeric" required />
            </label>
            <div className="field">
              <span>&nbsp;</span>
              <span className="muted" style={{ fontSize: 12.5, alignSelf: "center" }}>
                {t("structure.field.quantityHint", "Više od 1 pravi N pozicija sa istim nazivom i rednim brojem #1…#N (šifra dobija sufiks -1…-N).")}
              </span>
            </div>
          </div>
        )}
        {kindHoldsAsset && (
          <label className="field">
            <span>{t("structure.field.expectedType", "Očekivani tip")}</span>
            <AssetTypeCombobox value={expectedType} onChange={setExpectedType} />
          </label>
        )}
        {expectedChanged && hasRefValues && (
          <div style={noticeBox}>{t("structure.modal.expectedChangedNotice", "Promena očekivanog tipa briše postojeće referentne vrednosti.")}</div>
        )}
        <label className="field">
          <span>{t("structure.field.location", "Fizička lokacija")}</span>
          <Select<string> value={locationId} options={locationOptions} onChange={setLocationId} ariaLabel={t("structure.field.location", "Fizička lokacija")} />
        </label>
        <div className="form-grid">
          <label className="field">
            <span>{t("structure.field.drawingCode", "Šifra crteža")}</span>
            <input className="input" value={drawingCode} onChange={(e) => setDrawingCode(e.target.value)} maxLength={100} />
          </label>
          <label className="field">
            <span>{t("structure.field.sortOrder", "Redosled")}</span>
            <input className="input" type="number" value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} inputMode="numeric" />
          </label>
        </div>
        <label className="field">
          <span>{t("structure.field.interventionNote", "Napomena za intervenciju")}</span>
          <textarea className="input" rows={2} value={interventionNote} onChange={(e) => setInterventionNote(e.target.value)} style={textarea} placeholder={t("structure.field.interventionNotePlaceholder", "npr. Pre demontaže isključiti i zaključati MCC ormar (LOTO)")} />
        </label>
        <label className="field">
          <span>{t("structure.field.note", "Napomena")}</span>
          <textarea className="input" rows={3} value={note} onChange={(e) => setNote(e.target.value)} style={textarea} />
        </label>
        {error && <div style={errorBox} role="alert">{error}</div>}
        <div className="form-actions">
          <button type="button" className="btn" onClick={onClose}>{t("action.cancel", "Otkaži")}</button>
          <button type="submit" className="btn primary" disabled={submitting}>
            {submitting ? t("action.saving", "Čuvanje…") : editing ? t("action.save", "Sačuvaj") : t("action.create", "Kreiraj")}
          </button>
        </div>
      </form>
    </Modal>
  );
}
